
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CollaborationPermission } from "@/types/collaboration";
import { canViewResponses, canManageRecruitment, canManageTest } from "@/utils/collaboration";

interface TestPermissions {
  isOwner: boolean;
  isCollaborator: boolean;
  permission?: CollaborationPermission;
  canView: boolean;
  canManageRecruitment: boolean;
  canManageTest: boolean;
}

export function useTestPermissions(testId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['test-permissions', testId, user?.id],
    queryFn: async (): Promise<TestPermissions> => {
      if (!user?.id || !testId) {
        return {
          isOwner: false,
          isCollaborator: false,
          canView: false,
          canManageRecruitment: false,
          canManageTest: false,
        };
      }

      // Check if user is the test owner
      const { data: test, error: testError } = await supabase
        .from('tests')
        .select('creator_id')
        .eq('id', testId)
        .single();

      if (testError) throw testError;

      const isOwner = test.creator_id === user.id;

      if (isOwner) {
        return {
          isOwner: true,
          isCollaborator: false,
          canView: true,
          canManageRecruitment: true,
          canManageTest: true,
        };
      }

      // Check if user is a collaborator
      const { data: collaboration, error: collabError } = await supabase
        .from('test_collaborators')
        .select('permission')
        .eq('test_id', testId)
        .eq('collaborator_id', user.id)
        .eq('status', 'accepted')
        .single();

      if (collabError && collabError.code !== 'PGRST116') {
        throw collabError;
      }

      if (!collaboration) {
        return {
          isOwner: false,
          isCollaborator: false,
          canView: false,
          canManageRecruitment: false,
          canManageTest: false,
        };
      }

      return {
        isOwner: false,
        isCollaborator: true,
        permission: collaboration.permission,
        canView: canViewResponses(collaboration.permission),
        canManageRecruitment: canManageRecruitment(collaboration.permission),
        canManageTest: canManageTest(collaboration.permission),
      };
    },
    enabled: !!user?.id && !!testId,
  });
}
